import { Inject, Injectable, Optional } from '@angular/core';

import {
  Configuration,
  ConfigurationParameters,
} from '@renovationman/rm-uaa-api-client-ts-ng20';
import { IJwtUser, JwtUser } from '@renovationman/ts-utils';

import {
  ApiBasePaths,
  AuthLibConfig,
  AuthType,
  AUTH_LIB_CONFIG,
  ID_TOKEN_STORAGE_KEY,
  JWT_STORAGE_KEY,
  ORIGIN_URL_QUERY_PARAM_KEY,
  PREFILL_EMAIL_QUERY_PARAM_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
} from '../auth.config';
import { safeRemove } from '../storage';

export interface IJwtPayload {
  user: IJwtUser;
  exp: number;
}

// @dynamic
@Injectable({ providedIn: 'root' })
export class AuthService {
  appType: AuthType;

  constructor(
    @Optional()
    @Inject(AUTH_LIB_CONFIG)
    private authLibConfig: AuthLibConfig
  ) {
    this.appType = authLibConfig?.appType;
  }

  isLogged(): boolean {
    if (!this.jwtToken) {
      return false;
    }

    if (this.isTokenExpired()) {
      return false;
    }
    return true;
  }

  isTokenExpired() {
    return (
      this.jwtToken && this.getJwtPayload().exp * 1000 < new Date().getTime()
    );
  }

  private tokenValidityCheckingInterval;

  startTokenValidityChecking() {
    this.tokenValidityCheckingInterval = setInterval(
      () => this.logoutIfIsNotLogged(),
      1000
    );
  }

  endTokenValidityChecking() {
    if (this.tokenValidityCheckingInterval) {
      clearInterval(this.tokenValidityCheckingInterval);
    }
  }

  logoutIfIsNotLogged() {
    if (!this.isLogged()) {
      this.logout();
    }
  }

  get jwtToken(): string {
    return this.getJwtToken();
  }

  set jwtToken(jwtToken: string) {
    localStorage.setItem(JWT_STORAGE_KEY, jwtToken);
  }

  getJwtToken(): string {
    return localStorage.getItem(JWT_STORAGE_KEY);
  }

  goToLoginPage(destination?: string, prefillEmail?: string | null) {
    this.goToAuthPage('login', destination, prefillEmail);
  }

  getLoginPageUrl(prefillEmail?: string) {
    return this.getAuthPageUrl('login', false, prefillEmail).href;
  }

  private getAuthPageUrl(
    loginOrLogout: 'login' | 'logout',
    redirectTo: string | boolean = true,
    prefillEmail?: string | null
  ): URL {
    // `AUTH_LIB_CONFIG` est marqué `@Optional()`, et un consommateur s'en passe réellement :
    // rm-auth ne le fournit pas, puisqu'il EST le portail. Sans ce repli, `new URL(undefined)`
    // lève, et le `logout()` de son intercepteur d'erreurs échouait par une TypeError au lieu de
    // déconnecter. La même origine est précisément la bonne cible pour le portail.
    const url = new URL(this.authLibConfig?.authUrl || window.location.origin);
    url.pathname = '/' + loginOrLogout;
    // Sans garde, un consommateur sans `appType` produit littéralement `?type=undefined` : la
    // chaîne « undefined » n'est pas une valeur, et vaut moins que l'absence du paramètre.
    if (this.appType) {
      url.searchParams.set('type', this.appType);
    }
    if (redirectTo) {
      const redirectToUrl =
        typeof redirectTo === 'string' ? redirectTo : window.location.href;
      url.searchParams.set(ORIGIN_URL_QUERY_PARAM_KEY, redirectToUrl);
    }
    // Le chemin hérité a toujours su préremplir l'e-mail ; ce qui manquait, c'est de le faire depuis le
    // guard, qui est le seul à passer par ici quand une application renvoie quelqu'un se connecter. Sans
    // ça, les deux chemins — celui-ci et le flux OAuth — ne se comporteraient pas pareil selon qu'une
    // application est basculée ou non.
    if (prefillEmail) {
      url.searchParams.set(PREFILL_EMAIL_QUERY_PARAM_KEY, prefillEmail);
    }
    return url;
  }

  private goToAuthPage(
    loginOrLogout: 'login' | 'logout',
    redirectTo: string | boolean = true,
    prefillEmail?: string | null
  ) {
    window.location.href = this.getAuthPageUrl(
      loginOrLogout,
      redirectTo,
      prefillEmail
    ).href;
  }

  login(jwtToken: string) {
    this.jwtToken = jwtToken;
  }

  /**
   * Erase the token and go to login page
   *
   * @param redirectTo - Leave empty or true to set current URL as origin,
   * set false to not set any origin and set string to give custom origin
   */
  logout(redirectTo?: string | boolean) {
    this.eraseStoredToken();
    this.goToAuthPage('logout', redirectTo);
  }

  /**
   * Efface **les deux** jetons du navigateur.
   *
   * <strong>Le jeton de rafraîchissement n'est pas un détail qu'on oublie.</strong> Il vaut 30 jours,
   * et le serveur ne consulte aucune session pour l'échanger : le laisser derrière soi rend la
   * déconnexion fictive — la visite suivante obtient un jeton d'accès neuf sans redemander le mot de
   * passe, ce qui, sur un poste partagé, est une reprise de compte. Comme la fermeture de session
   * côté serveur ne révoque rien, c'est ici que la déconnexion se joue.
   *
   * Aucun des deux effacements ne peut échouer bruyamment : sur un stockage verrouillé, le second
   * doit être tenté même si le premier a levé.
   */
  eraseStoredToken() {
    safeRemove(JWT_STORAGE_KEY);
    safeRemove(REFRESH_TOKEN_STORAGE_KEY);
    safeRemove(ID_TOKEN_STORAGE_KEY);
  }

  configFactory(api: keyof ApiBasePaths): Configuration {
    const params: ConfigurationParameters = {
      basePath: this.authLibConfig?.apiBasePaths[api],
      accessToken: this.getJwtToken,
    };
    return new Configuration(params);
  }

  getJwtPayload(): IJwtPayload | null {
    const jwtToken = this.jwtToken;
    if (!jwtToken) {
      return null;
    }
    return this.parseJwt(jwtToken);
  }

  getJwtUser(): JwtUser | null {
    const jwtPayload = this.getJwtPayload();
    if (!jwtPayload) {
      return null;
    }
    return new JwtUser(jwtPayload.user);
  }

  private parseJwt(token) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  }
}
